import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../store';
import { logoutMdt } from '../lib/logoutMdt';
import { cn, API_BASE_URL } from '../lib/utils';
import { isMdtSelectableEvent } from '../lib/mdtSessionStorage';
import MdtLogoutConfirmModal from '../components/mobile/MdtLogoutConfirmModal';

interface Event {
  id: string;
  name: string;
  category: string;
  status: string;
  eventDate: string;
  location: { address: string };
}

interface MdtEventSelectProps {
  onSelect: (event: Event) => void;
}

export default function MdtEventSelect({ onSelect }: MdtEventSelectProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, token } = useAppSelector((state) => state.auth);
  const [events, setEvents] = useState<Event[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/events`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const live = (d.events || []).filter((evt: Event) => isMdtSelectableEvent(evt));
        setEvents(live);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]);

  const handleConfirm = () => {
    const evt = events.find((e) => e.id === selected);
    if (evt) {
      localStorage.setItem('c2_mdt_event', JSON.stringify(evt));
      onSelect(evt);
    }
  };

  const handleLogoutConfirm = async () => {
    setLoggingOut(true);
    try {
      await logoutMdt(dispatch, navigate, token);
    } finally {
      setLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  const statusColor: Record<string, string> = {
    open: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    supervision: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    active: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    closed: 'text-gray-400 bg-gray-400/10 border-gray-400/20',
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex flex-col items-center justify-center p-6">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-b from-blue-900/20 via-transparent to-transparent pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
            style={{ background: 'rgba(102,148,255,0.2)', border: '1px solid rgba(102,148,255,0.15)' }}
          >
            <Calendar className="w-7 h-7 text-blue-400" />
          </div>
          <h1 className="text-xl font-bold text-white">Select Event</h1>
          <p className="text-sm text-white/40 mt-1">
            Welcome, {user?.firstName}. Choose the event you are responding to.
          </p>
        </div>

        {/* Event list */}
        <div className="space-y-2 mb-6 max-h-[50vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-white/30 text-sm">Loading events...</div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-white/30 text-sm">
              No open events available — contact Command if you need access.
            </div>
          ) : (
            events.map((evt) => (
              <button
                key={evt.id}
                onClick={() => setSelected(evt.id)}
                className={cn(
                  'w-full flex items-center gap-3 p-3.5 rounded-xl text-left transition-all active:scale-[0.98]',
                  selected === evt.id
                    ? 'bg-blue-500/15 border-[1.5px] border-blue-400/40'
                    : 'border-[1.5px] border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
                )}
              >
                <div className={cn(
                  'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
                  selected === evt.id ? 'bg-blue-500/20' : 'bg-white/[0.04]'
                )}>
                  <Calendar className={cn('w-5 h-5', selected === evt.id ? 'text-blue-400' : 'text-white/30')} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn('text-sm font-bold', selected === evt.id ? 'text-white' : 'text-white/80')}>
                      {evt.name}
                    </span>
                    <span className={cn('text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded border', statusColor[evt.status] || 'text-gray-400')}>
                      {evt.status}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/30 mt-0.5">
                    {evt.category} &middot; {evt.eventDate} &middot; {evt.id}
                  </div>
                  <div className="text-[10px] text-white/25 mt-0.5 truncate">
                    {evt.location?.address || 'No location'}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Buttons */}
        <div className="space-y-2">
          {/* Confirm button */}
          <button
            onClick={handleConfirm}
            disabled={!selected}
            className={cn(
              'w-full py-3 rounded-xl text-sm font-bold transition-all',
              selected
                ? 'bg-blue-500 text-white hover:bg-blue-400 active:scale-[0.98]'
                : 'bg-white/[0.04] text-white/20 border border-white/[0.06] cursor-not-allowed'
            )}
          >
            {selected ? 'Select Event' : 'Select an event to continue'}
          </button>

          {/* Logout button */}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full py-3 rounded-xl text-sm font-bold transition-all bg-red-500/10 text-red-400 border border-red-400/20 hover:bg-red-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>
        </div>

        <MdtLogoutConfirmModal
          open={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onConfirm={handleLogoutConfirm}
          loggingOut={loggingOut}
        />

        <p className="text-center text-[10px] text-white/20 mt-4">
          You will select your unit after choosing an event.
        </p>
      </div>
    </div>
  );
}
